import { MAIRKO_CHAT } from "./mairko-chat.js";
import type { ChatCompanyResearch, ChatMessage } from "./messages.js";
import { agentById } from "./nav.js";
import type { OnboardingPackRow } from "./onboarding-api.js";

const STORAGE_KEY = "mairko-matrix-onboarding";

export type OnboardingStepId =
	| "welcome"
	| "website"
	| "researching"
	| "findings"
	| "complete";

export type OnboardingProfile = {
	firstName: string;
	lastName: string;
	email: string;
	company: string;
	website: string;
	packId?: string;
};

export type OnboardingSession = {
	step: OnboardingStepId;
	profile: Partial<OnboardingProfile>;
	startedAt: string;
	completedAt?: string;
};

/** Ordered collect steps shown in the progress rail. */
export const COLLECT_STEPS: readonly OnboardingStepId[] = ["website"] as const;

const STEP_META: Record<
	OnboardingStepId,
	{ label: string; placeholder?: string; inputMode?: "text" | "email" | "url" }
> = {
	welcome: { label: "Welcome" },
	website: {
		label: "Website",
		placeholder: "https://yourcompany.com",
		inputMode: "url",
	},
	researching: { label: "Research" },
	findings: { label: "Findings" },
	complete: { label: "Done" },
};

export function stepMeta(step: OnboardingStepId) {
	return STEP_META[step];
}

export function progressFor(step: OnboardingStepId): {
	current: number;
	total: number;
	label: string;
} {
	const total = 3;
	switch (step) {
		case "welcome":
		case "website":
			return { current: 1, total, label: "1 of 3 · Website" };
		case "researching":
			return { current: 2, total, label: "2 of 3 · Researching" };
		case "findings":
			return { current: 2, total, label: "2 of 3 · Review findings" };
		case "complete":
			return { current: 3, total, label: "Setup complete" };
		default: {
			const _exhaustive: never = step;
			return _exhaustive;
		}
	}
}

export function isOnboardingComplete(session: OnboardingSession): boolean {
	return session.step === "complete" && Boolean(session.completedAt);
}

export function emptySession(): OnboardingSession {
	return {
		step: "welcome",
		profile: {},
		startedAt: new Date().toISOString(),
	};
}

export function readOnboardingSession(): OnboardingSession | null {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		if (!raw) return null;
		const parsed: unknown = JSON.parse(raw);
		if (!parsed || typeof parsed !== "object") return null;
		const session = parsed as OnboardingSession;
		if (typeof session.step !== "string" || !session.profile) return null;
		// Drop the old name/email questionnaire mid-flight.
		if (
			session.step === ("first_name" as OnboardingStepId) ||
			session.step === ("last_name" as OnboardingStepId) ||
			session.step === ("email" as OnboardingStepId) ||
			session.step === ("company" as OnboardingStepId)
		) {
			return { ...session, step: "website" };
		}
		return session;
	} catch {
		return null;
	}
}

export function writeOnboardingSession(session: OnboardingSession): void {
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
	} catch {
		/* private mode */
	}
}

export function clearOnboardingSession(): void {
	try {
		localStorage.removeItem(STORAGE_KEY);
	} catch {
		/* private mode */
	}
}

function agentMsg(body: string, card?: ChatMessage["card"]): ChatMessage {
	const agent = agentById("onboarding");
	return {
		id: crypto.randomUUID(),
		agentId: "onboarding",
		author: "agent",
		authorName: agent.name,
		avatar: agent.avatar,
		body,
		at: new Date().toISOString(),
		card,
	};
}

export function welcomeMessages(firstName?: string): ChatMessage[] {
	const hello = firstName?.trim() ? `Hey ${firstName.trim()}.` : "Hey.";
	return [
		agentMsg(
			`${hello} Paste the site whenever — or just tell me what you do.`,
		),
	];
}

export function resumeMessages(
	session: OnboardingSession,
	firstName?: string,
): ChatMessage[] {
	if (session.step === "welcome") return welcomeMessages(firstName);
	if (session.step === "complete") {
		return [
			agentMsg(
				`You’re in. ${session.profile.company ?? "The workspace"} is set up from the company pack. Connect Slack or WhatsApp when you’re ready so approvals have a home.`,
			),
		];
	}
	if (session.step === "findings") {
		return [
			agentMsg(
				"Welcome back — the company pack is waiting on your review in the card above. Does it feel roughly right?",
			),
		];
	}
	if (session.step === "researching") {
		return [
			agentMsg(
				`Still learning ${session.profile.website ?? "the brand"} — I’ll come back with what I found.`,
			),
		];
	}
	return [
		agentMsg("Welcome back — paste the site whenever."),
	];
}

export function startOnboarding(session: OnboardingSession): {
	session: OnboardingSession;
	messages: ChatMessage[];
} {
	return {
		session: {
			...session,
			step: "website",
			startedAt: session.startedAt || new Date().toISOString(),
		},
		messages: [
			agentMsg("Paste the site whenever you’re ready."),
		],
	};
}

export function displayName(session: OnboardingSession): string {
	const first = session.profile.firstName?.trim();
	if (first) return first;
	return "You";
}

export function composerEnabled(_step: OnboardingStepId): boolean {
	return true;
}

/**
 * Yes / “clone it” need a URL. The invite often has the brand name only —
 * the pack card or an earlier paste still has the site.
 */
export function resolveWorkingCopyUrl(input: {
	askUrl?: string;
	packUrl?: string;
	profileUrl?: string;
	messages: ReadonlyArray<{ author: string; body: string }>;
}): string {
	for (const raw of [input.askUrl, input.packUrl, input.profileUrl]) {
		const trimmed = raw?.trim();
		if (trimmed) return trimmed;
	}
	for (let i = input.messages.length - 1; i >= 0; i--) {
		const row = input.messages[i];
		if (row?.author !== "human") continue;
		const href = extractOnboardingUrl(row.body);
		if (href) return href;
	}
	return "";
}

/** Bare domain or https URL in a first-run paste. */
export function extractOnboardingUrl(text: string): string | null {
	const trimmed = text.trim();
	if (!trimmed) return null;
	const match = trimmed.match(
		/(?:https?:\/\/)?(?:www\.)?((?:[a-z0-9-]+\.)+[a-z]{2,})(?:\/[^\s]*)?/i,
	);
	const host = match?.[1]?.toLowerCase().replace(/[.,);:]+$/g, "");
	if (!host || host.split(".").length < 2) return null;
	return `https://${host}`;
}

/**
 * Whole-site build commands only (“design me a new site”, “redesign the whole
 * site”) — starts the build without a model turn. Anything scoped to part of a
 * page (“redesign the navbar”, “make the header black”) must reach the model,
 * which routes it to Rhea via edit_hosted_site instead of a full rebuild.
 */
export function parseWorkingCopyCommand(text: string): {
	url: string | null;
} | null {
	const trimmed = text.trim();
	const siteScale =
		/\b(?:working copy|new (?:web)?site|(?:redesign|rebuild) (?:the |my |our )?(?:whole |entire )?(?:web)?site\b|(?:build|design|make) (?:me |us )?a (?:new |whole new )?(?:web)?site\b)/i;
	if (!siteScale.test(trimmed)) return null;
	// “redesign the site's navbar” is still an edit — a page-part word anywhere
	// in the message means this is not a whole-site rebuild.
	const pagePart =
		/\b(nav(?:bar)?|header|footer|hero|menu|button|logo|colou?r|font|section|page|banner|form|link|text|(?<!working )copy|image|photo)s?\b/i;
	if (pagePart.test(trimmed)) return null;
	return { url: extractOnboardingUrl(trimmed) };
}

export function completeOnboardingSession(
	session: OnboardingSession,
	args: { company: string; website: string },
): OnboardingSession {
	return {
		...session,
		step: "complete",
		completedAt: new Date().toISOString(),
		profile: {
			...session.profile,
			company: args.company,
			website: args.website,
		},
	};
}

function phaseFromPackStatus(status: string): ChatCompanyResearch["phase"] {
	switch (status) {
		case "ready":
		case "confirmed":
			return "ready";
		case "failed":
			return "failed";
		case "researching":
			return "progress";
		default:
			return "started";
	}
}

export function researchFromPackRow(
	row: OnboardingPackRow,
): ChatCompanyResearch {
	return {
		packId: row.packId,
		siteUrl: row.siteUrl,
		phase: phaseFromPackStatus(row.status),
		status: row.status,
		progress: row.progress,
		brandName: row.card?.brandName,
		lastError: row.lastError,
		card: row.card,
	};
}

/** Resume an in-flight pack in the main chat. Empty = designed intro, not a bubble. */
export function seedMainChatOnboarding(args: {
	latest: OnboardingPackRow | null;
}): ChatMessage[] {
	const latest = args.latest;
	if (!latest) return [];
	const research = researchFromPackRow(latest);
	if (latest.status === "confirmed") {
		return [
			{
				id: crypto.randomUUID(),
				agentId: "mairko",
				author: "agent",
				authorName: MAIRKO_CHAT.name,
				body: "",
				at: new Date().toISOString(),
				companyResearch: [research],
			},
		];
	}
	if (latest.status === "ready") {
		return [
			{
				id: crypto.randomUUID(),
				agentId: "mairko",
				author: "agent",
				authorName: MAIRKO_CHAT.name,
				body: "The pack is ready. Does this look right?",
				at: new Date().toISOString(),
				companyResearch: [research],
			},
		];
	}
	if (latest.status === "researching" || latest.status === "pending") {
		return [
			{
				id: crypto.randomUUID(),
				agentId: "mairko",
				author: "agent",
				authorName: MAIRKO_CHAT.name,
				body: `Learning ${latest.siteUrl.replace(/^https?:\/\//i, "")}…`,
				at: new Date().toISOString(),
				companyResearch: [research],
			},
		];
	}
	return [];
}
